// src/components/dashboard/RecentCalls.js
import React from 'react';
import styled from 'styled-components';
import { 
  FaPhoneAlt, 
  FaPhoneSlash, 
  FaLongArrowAltLeft, 
  FaLongArrowAltRight,
  FaPlay,
  FaUser
} from 'react-icons/fa';
import DashboardCard from './DashboardCard';
import { Link } from 'react-router-dom';

const CallsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

const CallItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing(2)};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(4px);
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${props => props.theme.spacing(1)};
  }
`;

const CallIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => 
    props.direction === 'inbound' 
      ? props.theme.colors.success + '22'
      : props.theme.colors.primary + '22'
  };
  color: ${props => 
    props.direction === 'inbound' 
      ? props.theme.colors.success
      : props.theme.colors.primary
  };
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${props => props.theme.spacing(2)};
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    margin-right: 0;
    margin-bottom: ${props => props.theme.spacing(1)};
  }
`;

const CallInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CallHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  margin-bottom: ${props => props.theme.spacing(0.5)};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${props => props.theme.spacing(0.5)};
  }
`;

const CallName = styled(Link)`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

const CallTime = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
`;

const CallDetails = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const DirectionIndicator = styled.span`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
`;

const CallDuration = styled.span`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
`;

const PlayButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary}22;
  }
  
  @media (max-width: 480px) {
    margin-left: 0;
    margin-top: ${props => props.theme.spacing(1)};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing(4)};
  color: ${props => props.theme.colors.textSecondary};
`;

// Helper to format phone numbers
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

// Helper to format duration
const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Helper to format date
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString();
};

const RecentCalls = ({ data, loading, error }) => {
  const API_URL = process.env.REACT_APP_API_PUBLIC;

  const handlePlayRecording = (recording, e) => {
    e.preventDefault();
    
    // Create an audio element and play the recording
    if (recording) {
      const audio = new Audio(`${API_URL}${recording}`);
      console.log(audio);
      audio.play();
    }
  };

  return (
    <DashboardCard
      title="Recent Calls"
      icon={<FaPhoneAlt />}
      viewAllLink="/communications/all"
      loading={loading}
      error={error}
    >
      {data && data.length > 0 ? (
        <CallsList>
          {data.map((call, index) => (
            <CallItem key={`call-${call.call_id || index}`}>
              <CallIcon direction={call.direction}>
                {call.direction === 'inbound' ? <FaPhoneAlt /> : <FaPhoneSlash />}
              </CallIcon>
              
              <CallInfo>
                <CallHeader>
                  {call.Lead ? (
                    <CallName to={`/leads/view/${call.Lead.id}`}>
                      <FaUser />
                      {call.Lead.name}
                    </CallName>
                  ) : (
                    <CallName to={`/communications/placeholder-leads/${call.from_number}`}>
                      {formatPhoneNumber(call.from_number)}
                    </CallName>
                  )}
                  
                  <CallTime>{formatDateTime(call.call_time)}</CallTime>
                </CallHeader>
                
                <CallDetails>
                  <DirectionIndicator>
                    {call.direction === 'inbound' ? (
                      <>
                        <FaLongArrowAltLeft />
                        Incoming
                      </>
                    ) : (
                      <>
                        <FaLongArrowAltRight />
                        Outgoing
                      </>
                    )}
                  </DirectionIndicator>
                  
                  <CallDuration>
                    Duration: {formatDuration(call.duration)}
                  </CallDuration>
                </CallDetails>
              </CallInfo>
              
              {call.recording_path && (
                <PlayButton 
                  onClick={(e) => handlePlayRecording(call.recording_path, e)}
                  title="Play Recording"
                >
                  <FaPlay />
                </PlayButton>
              )}
            </CallItem>
          ))}
        </CallsList>
      ) : (
        <EmptyState>No recent calls found</EmptyState>
      )}
    </DashboardCard>
  );
};

export default RecentCalls;